import { useEffect, useRef, useState } from 'react';

import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';

import DeleteIcon from '@/components/icons/delete';
import GripDotVerticalIcon from '@/components/icons/grip-dot-vertical';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ItemBase {
  id: string;
}

interface UpdateWorkspaceEntityDialogConfig<T extends ItemBase> {
  title: string;
  description: string;
  createEmptyItem: () => T;
  getValue: (item: T) => string;
  setValue: (item: T, value: string) => T;
  // Defaults to always-deletable when omitted — matches today's behaviour for
  // Issue categories, Schedule tags, and the table-columns picker.
  isDeletable?: (item: T) => boolean;
  // Per-item, so the reason a specific item can't be deleted can vary (e.g. a
  // permanent system category vs. one currently in use).
  nonDeletableTooltip?: (item: T) => string;
}

function UpdateWorkspaceEntityDialog<T extends ItemBase>({
  items,
  open,
  onOpenChange,
  callback,
  config,
}: {
  items: T[] | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback: (items: T[]) => void;
  config: UpdateWorkspaceEntityDialogConfig<T>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Bottom clearance = max(30px, iOS home-indicator inset) so the footer buttons never sit under the home indicator; env() resolves to 0 on devices without one. */}
      <DialogContent className="flex max-h-[calc(100dvh_-_100px_-_max(30px,_env(safe-area-inset-bottom,_0px)))] flex-col gap-[10px] overflow-hidden">
        <DialogTitle>{config.title}</DialogTitle>

        <DialogDescription>{config.description}</DialogDescription>

        <UpdateWorkspaceEntityDialogControls items={items} onOpenChange={onOpenChange} callback={callback} config={config} />
      </DialogContent>
    </Dialog>
  );
}

function UpdateWorkspaceEntityDialogControls<T extends ItemBase>({
  items,
  onOpenChange,
  callback,
  config,
}: {
  items: T[] | undefined;
  onOpenChange: (open: boolean) => void;
  callback: (items: T[]) => void;
  config: UpdateWorkspaceEntityDialogConfig<T>;
}) {
  const initial = items && items.length > 0 ? items : [config.createEmptyItem()];

  const [itemListRef, list, setList] = useDragAndDrop<HTMLUListElement, T>(initial, {
    dragHandle: '.handle',
    sortable: true,
    dragPlaceholderClass: 'rounded-md bg-grey-1 p-1',
    draggingClass: 'rounded-md bg-grey-1 p-1 shadow-lg scale-105',
  });

  const [errors, setErrors] = useState<Set<string>>(new Set());
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [shouldFocusLastItem, setShouldFocusLastItem] = useState(false);

  function validateItems(): boolean {
    const newErrors = new Set<string>();
    for (const item of list) {
      const value = config.getValue(item);
      if (!value.trim()) newErrors.add(item.id);
    }
    setErrors(newErrors);
    return newErrors.size === 0;
  }

  function handleDone() {
    if (validateItems()) {
      callback(list);
      onOpenChange(false);
    }
  }

  function handleEditChange(itemId: string, newValue: string) {
    setList((prev) => prev.map((it) => (it.id === itemId ? config.setValue(it, newValue) : it)));

    setErrors((prev) => {
      if (!prev.has(itemId)) return prev;
      if (!newValue.trim()) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }

  function handleRemoveItem(itemId: string) {
    setList((prev) => prev.filter((it) => it.id !== itemId));
    setErrors((prev) => {
      if (!prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }

  function handleAddItem() {
    setList((prev) => [...prev, config.createEmptyItem()]);
    setShouldFocusLastItem(true);
  }

  useEffect(() => {
    if (items === undefined || items.length === 0) {
      setShouldFocusLastItem(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldFocusLastItem && list.length > 0) {
      const lastItem = list[list.length - 1];
      const inputElement = inputRefs.current.get(lastItem.id);
      if (inputElement) {
        inputElement.focus();
        setShouldFocusLastItem(false);
      }
    }
  }, [list, shouldFocusLastItem]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pr-2">
        <ul ref={itemListRef} className="space-y-2">
          {list.map((item) => {
            const deletable = config.isDeletable ? config.isDeletable(item) : true;

            return (
              <li key={item.id} className="bg-grey-1 rounded-md p-1">
                <div className="flex items-center gap-1">
                  <GripDotVerticalIcon className={cn('handle text-secondary-text cursor-grab', errors.has(item.id) && 'mb-4')} />

                  <div className="flex flex-1 flex-col gap-1">
                    <Input
                      ref={(el) => {
                        if (el) {
                          inputRefs.current.set(item.id, el);
                        } else {
                          inputRefs.current.delete(item.id);
                        }
                      }}
                      value={config.getValue(item)}
                      onChange={(e) => handleEditChange(item.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddItem();
                        }
                      }}
                      className={cn('flex-1', errors.has(item.id) && 'border-default-red')}
                      placeholder="Type text ..."
                    />
                    {errors.has(item.id) && <span className="text-default-red text-sm">Required</span>}
                  </div>

                  {deletable ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label="Remove item"
                      onClick={() => handleRemoveItem(item.id)}
                      className={cn('text-default-red p-0', errors.has(item.id) && 'mb-4')}>
                      <DeleteIcon />
                    </Button>
                  ) : (
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <div className={cn(errors.has(item.id) && 'mb-4')}>
                            <Button type="button" variant="ghost" size="sm" aria-label="Remove item" disabled className="text-default-red p-0 opacity-50">
                              <DeleteIcon />
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipPortal>
                          <TooltipContent side="top" className="bg-default-text pointer-events-none border-none text-xs text-white">
                            <TooltipArrow className="fill-default-text" />
                            {config.nonDeletableTooltip?.(item) ?? "This item can't be deleted"}
                          </TooltipContent>
                        </TooltipPortal>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-2 flex flex-col gap-2">
        <Button onClick={handleAddItem} className="mr-auto">
          Add new
        </Button>
        <Button variant="success" onClick={handleDone} className="mr-auto">
          Done
        </Button>
      </div>
    </div>
  );
}

export { UpdateWorkspaceEntityDialog, type UpdateWorkspaceEntityDialogConfig };
